import React, {useEffect, useRef} from 'react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Reuse a single loader promise so the script isn't injected twice.
function loadGoogleMapsScript(apiKey) {
    if (window.google?.maps) return Promise.resolve();
    if (window.__gmapsLoaderPromise) return window.__gmapsLoaderPromise;

    window.__gmapsLoaderPromise = new Promise((resolve, reject) => {
        const existing = document.getElementById('google-maps-script');
        if (existing) {
            existing.addEventListener('load', resolve);
            existing.addEventListener('error', reject);
            return;
        }
        const script = document.createElement('script');
        script.id = 'google-maps-script';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
    });
    return window.__gmapsLoaderPromise;
}

/**
 * Renders a Google Map with a draggable marker (center) and a circle (radius).
 * - `center`: { lat, lng }
 * - `radiusKm`: number (kilometres)
 * - `onCenterChange(lat, lng)`: fired when the user drags the marker / clicks the map
 */
const ZoneCircleMap = ({center, radiusKm = 0, onCenterChange}) => {
    const mapDivRef = useRef(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const circleRef = useRef(null);

    const hasCenter =
        center && Number.isFinite(Number(center.lat)) && Number.isFinite(Number(center.lng));

    // Initialise the map exactly once.
    useEffect(() => {
        let cancelled = false;
        const init = async () => {
            if (!GOOGLE_MAPS_API_KEY) return;
            await loadGoogleMapsScript(GOOGLE_MAPS_API_KEY);
            if (cancelled || !mapDivRef.current || mapRef.current) return;

            const initialCenter = hasCenter
                ? {lat: Number(center.lat), lng: Number(center.lng)}
                : {lat: 20.5937, lng: 78.9629}; // India centroid fallback

            mapRef.current = new window.google.maps.Map(mapDivRef.current, {
                center: initialCenter,
                zoom: hasCenter ? 13 : 5,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false
            });

            markerRef.current = new window.google.maps.Marker({
                position: initialCenter,
                map: mapRef.current,
                draggable: true,
                visible: hasCenter
            });

            circleRef.current = new window.google.maps.Circle({
                map: mapRef.current,
                center: initialCenter,
                radius: Math.max(0, Number(radiusKm) || 0) * 1000,
                strokeColor: '#C2410C',
                strokeOpacity: 0.9,
                strokeWeight: 2,
                fillColor: '#FB923C',
                fillOpacity: 0.18,
                visible: hasCenter && Number(radiusKm) > 0
            });

            markerRef.current.addListener('dragend', (e) => {
                onCenterChange?.(e.latLng.lat(), e.latLng.lng());
            });

            mapRef.current.addListener('click', (e) => {
                onCenterChange?.(e.latLng.lat(), e.latLng.lng());
            });
        };
        init();
        return () => {
            cancelled = true;
        };
    }, []);

    // Keep marker / circle / map view in sync with props.
    useEffect(() => {
        if (!mapRef.current || !markerRef.current || !circleRef.current) return;
        if (hasCenter) {
            const pos = {lat: Number(center.lat), lng: Number(center.lng)};
            markerRef.current.setPosition(pos);
            markerRef.current.setVisible(true);
            circleRef.current.setCenter(pos);
            mapRef.current.panTo(pos);
            const z = mapRef.current.getZoom();
            if (!z || z < 11) mapRef.current.setZoom(13);
        } else {
            markerRef.current.setVisible(false);
            circleRef.current.setVisible(false);
        }
    }, [center?.lat, center?.lng, hasCenter]);

    useEffect(() => {
        if (!circleRef.current) return;
        const r = Math.max(0, Number(radiusKm) || 0);
        circleRef.current.setRadius(r * 1000);
        circleRef.current.setVisible(hasCenter && r > 0);
    }, [radiusKm, hasCenter]);

    if (!GOOGLE_MAPS_API_KEY) {
        return (
            <div className="border rounded-lg p-4 bg-yellow-50 text-yellow-800 text-sm">
                Google Maps API key not configured (REACT_APP_GOOGLE_MAPS_API_KEY).
            </div>
        );
    }

    return (
        <div
            ref={mapDivRef}
            data-testid="zone-circle-map"
            style={{width: '100%', height: 360, borderRadius: 8, overflow: 'hidden'}}
            className="border"
        />
    );
};

export default ZoneCircleMap;
